import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from '../../../AxiosInstance';
import Modal from '../../../components/modal/modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faImage, faUpload } from '@fortawesome/free-solid-svg-icons';
import {useTranslation} from "react-i18next";

export default function RecordFormModal({ show, setter, machine, plan, record, onSaved }) {
    const {t} = useTranslation();
    const project_id = parseInt(localStorage.getItem('project_id'));
    const isEdit = !!record;

    const [form, setForm] = useState({
        machine: machine?.id || '',
        project: project_id,
        plan: plan?.id || '',
        performed_at: new Date().toISOString().slice(0, 16),
        notes: '',
    });
    const [plans, setPlans] = useState([]);
    const [planSearch, setPlanSearch] = useState('');
    const [machineSearch, setMachineSearch] = useState('');
    const [allMachines, setAllMachines] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [parts, setParts] = useState([]);
    const [catalogParts, setCatalogParts] = useState([]);
    const [newPart, setNewPart] = useState({ name: '', brand: '', part_number: '', quantity: 1, spare_part: '' });
    const [saving, setSaving] = useState(false);
    const [prefilledFromPlan, setPrefilledFromPlan] = useState(false);
    const photoInputRef = useRef();

    useEffect(() => {
        axiosInstance.get(`maintenance_plan/?project_id=${project_id}`).then(res => {
            setPlans(res.data);
        });
        axiosInstance.get(`spare_part/?project_id=${project_id}`).then(res => {
            setCatalogParts(res.data);
        });
        axiosInstance.get(`machine/?project_id=${project_id}`).then(res => {
            setAllMachines((res.data || []).filter(m => !m.deleted_at));
        });
    }, [project_id]);

    // Pre-fill from existing record (edit mode)
    useEffect(() => {
        if (isEdit && record) {
            setForm({
                machine: record.machine || machine?.id || '',
                project: project_id,
                plan: record.plan || '',
                performed_at: record.performed_at ? record.performed_at.slice(0, 16) : new Date().toISOString().slice(0, 16),
                notes: record.notes || '',
            });
            setParts((record.parts_used || []).map(p => ({
                id: p.id,
                name: p.name,
                brand: p.brand || '',
                part_number: p.part_number || '',
                quantity: p.quantity,
                spare_part: p.spare_part || '',
                machine_stock: p.machine_stock || '',
                _existing: true,
            })));
            setPrefilledFromPlan(false);
        }
    }, [record?.id]);

    const selectedPlan = plans.find(p => p.id === parseInt(form.plan));
    const selectedMachineId = parseInt(form.machine) || '';

    // Machines available in the machine selector:
    // - If a plan is selected → only the machines assigned to that plan
    // - If no plan → all project machines
    const availableMachines = selectedPlan
        ? allMachines.filter(m => (selectedPlan.machines || []).includes(m.id))
        : allMachines;

    const resolveMachineStock = (spare_part_id, machineId) => {
        const found = catalogParts.find(p => p.id === parseInt(spare_part_id));
        return (found?.stock_by_machine || []).find(s => s.machine_id === (machineId || form.machine))?.stock_entry_id || '';
    };

    // Pre-fill parts from plan prop (when opened via "Do Maintenance" button)
    useEffect(() => {
        if (!isEdit && plan?.recommended_parts?.length && catalogParts.length >= 0) {
            setParts(plan.recommended_parts.map(p => ({
                name: p.name,
                brand: p.brand || '',
                part_number: p.part_number || '',
                quantity: p.quantity,
                spare_part: p.spare_part || '',
                machine_stock: resolveMachineStock(p.spare_part, machine?.id),
            })));
            setPrefilledFromPlan(true);
        }
    }, [plan?.id, catalogParts]);

    const handlePhotoAdd = (e) => {
        const files = Array.from(e.target.files);
        setPhotos(prev => [...prev, ...files.map(f => ({ file: f, caption: '', preview: URL.createObjectURL(f) }))]);
    };

    const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

    const selectCatalogPart = (partId) => {
        const found = catalogParts.find(p => p.id === parseInt(partId));
        if (found) {
            setNewPart({
                name: found.name,
                brand: found.brand || '',
                part_number: found.part_number || '',
                quantity: 1,
                spare_part: found.id,
                machine_stock: resolveMachineStock(found.id, form.machine),
            });
        } else {
            setNewPart({ name: '', brand: '', part_number: '', quantity: 1, spare_part: '' });
        }
    };

    const addPart = () => {
        if (!newPart.name.trim()) return;
        setParts(prev => [...prev, { ...newPart }]);
        setNewPart({ name: '', brand: '', part_number: '', quantity: 1, spare_part: '' });
    };

    const removePart = (idx) => setParts(prev => prev.filter((_, i) => i !== idx));

    const handleSave = async () => {
        if (!form.machine) return;
        setSaving(true);
        try {
            if (isEdit) {
                await axiosInstance.patch(`maintenance_record/${record.id}/`, {
                    machine: parseInt(form.machine),
                    plan: form.plan || null,
                    performed_at: form.performed_at,
                    notes: form.notes,
                });

                const originalIds = (record.parts_used || []).map(p => p.id);
                const remainingIds = parts.filter(p => p._existing).map(p => p.id);
                const removedIds = originalIds.filter(id => !remainingIds.includes(id));
                const newParts = parts.filter(p => !p._existing);

                for (const id of removedIds) {
                    await axiosInstance.delete(`maintenance_record/${record.id}/remove_part/?part_id=${id}`);
                }
                for (const p of newParts) {
                    await axiosInstance.post(`maintenance_record/${record.id}/add_part/`, {
                        name: p.name,
                        brand: p.brand,
                        part_number: p.part_number,
                        quantity: parseInt(p.quantity) || 1,
                        spare_part: p.spare_part || null,
                        machine_stock: p.machine_stock || null,
                        record: record.id,
                    });
                }
            } else {
                const res = await axiosInstance.post('maintenance_record/', {
                    machine: parseInt(form.machine),
                    project: project_id,
                    plan: form.plan || null,
                    performed_at: form.performed_at,
                    notes: form.notes,
                });
                const recordId = res.data.id;

                for (const p of photos) {
                    const fd = new FormData();
                    fd.append('image', p.file);
                    if (p.caption) fd.append('caption', p.caption);
                    await axiosInstance.post(`maintenance_record/${recordId}/add_photo/`, fd);
                }

                for (const p of parts) {
                    await axiosInstance.post(`maintenance_record/${recordId}/add_part/`, {
                        name: p.name,
                        brand: p.brand,
                        part_number: p.part_number,
                        quantity: parseInt(p.quantity) || 1,
                        spare_part: p.spare_part || null,
                        machine_stock: p.machine_stock || null,
                        record: recordId,
                    });
                }
            }

            onSaved();
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const handlePlanChange = (planId) => {
        const newSelectedPlan = plans.find(p => p.id === parseInt(planId));
        const newMachines = newSelectedPlan ? allMachines.filter(m => (newSelectedPlan.machines || []).includes(m.id)) : allMachines;
        // Auto-select if only one machine available for this plan
        const autoMachine = newSelectedPlan && newMachines.length === 1 ? newMachines[0].id : '';
        setForm(prev => ({ ...prev, plan: planId, machine: autoMachine }));

        if (!isEdit && planId && newSelectedPlan?.recommended_parts?.length) {
            setParts(newSelectedPlan.recommended_parts.map(p => ({
                name: p.name,
                brand: p.brand || '',
                part_number: p.part_number || '',
                quantity: p.quantity,
                spare_part: p.spare_part || '',
                machine_stock: resolveMachineStock(p.spare_part, autoMachine),
            })));
            setPrefilledFromPlan(true);
        } else if (!isEdit && prefilledFromPlan) {
            setParts([]);
            setPrefilledFromPlan(false);
        }
    };

    const filteredPlans = plans.filter(p =>
        p.name.toLowerCase().includes(planSearch.toLowerCase())
    );

    const catalogOptions = [{ id: '', name: t('maintenance.records.selectFromCatalog') }, ...catalogParts.map(p => ({ id: p.id, name: `${p.name}${p.part_number ? ' (' + p.part_number + ')' : ''}` }))];

    return (
        <Modal
            show={show}
            setter={setter}
            name={isEdit ? t('maintenance.editRecord') : t('maintenance.createRecord')}
            description={machine?.name}
            buttonName={saving ? t('common.saving') : t('common.save')}
            onClick={handleSave}
            disabled={!form.machine}
        >
            {/* Plan selector */}
            <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '13px', color: '#64748B', display: 'block', marginBottom: '4px' }}>{t('maintenance.records.plan')}</label>
                <input
                    type="text"
                    placeholder={t('maintenance.searchPlan', 'Buscar plan...')}
                    value={planSearch}
                    onChange={e => setPlanSearch(e.target.value)}
                    style={{ width: '100%', padding: '7px 12px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', marginBottom: '6px', boxSizing: 'border-box', outline: 'none', color: '#334155' }}
                />
                <div style={{ border: '1px solid #E2E8F0', borderRadius: '6px', maxHeight: '160px', overflowY: 'auto' }}>
                    {/* Ad-hoc option */}
                    <div
                        onClick={() => handlePlanChange('')}
                        style={{
                            padding: '7px 10px', cursor: 'pointer', fontSize: '13px',
                            color: !form.plan ? '#4F46E5' : '#64748B',
                            background: !form.plan ? '#EEF2FF' : 'transparent',
                            fontStyle: 'italic',
                        }}
                    >
                        {t('maintenance.records.adHocNoPlan')}
                    </div>
                    {filteredPlans.map(p => (
                        <div
                            key={p.id}
                            onClick={() => handlePlanChange(String(p.id))}
                            style={{
                                padding: '7px 10px', cursor: 'pointer',
                                borderTop: '1px solid #F1F5F9', fontSize: '13px',
                                color: parseInt(form.plan) === p.id ? '#4F46E5' : '#334155',
                                background: parseInt(form.plan) === p.id ? '#EEF2FF' : 'transparent',
                                fontWeight: parseInt(form.plan) === p.id ? 600 : 400,
                            }}
                        >
                            {p.name}
                        </div>
                    ))}
                    {filteredPlans.length === 0 && planSearch && (
                        <div style={{ padding: '10px', color: '#94A3B8', fontSize: '13px', textAlign: 'center' }}>
                            {t('maintenance.noMachines', 'Sin resultados')}
                        </div>
                    )}
                </div>
                {form.plan && selectedPlan && (
                    <div className="d-flex flex-wrap gap-1" style={{ marginTop: '6px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: '12px', background: '#EEF2FF', color: '#4F46E5', fontSize: '12px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            {selectedPlan.name}
                            <span style={{ cursor: 'pointer', fontSize: '10px', lineHeight: 1 }} onClick={() => handlePlanChange('')}>×</span>
                        </span>
                    </div>
                )}
            </div>

            {/* Machine selector */}
            <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '13px', color: '#64748B', display: 'block', marginBottom: '4px' }}>
                    {t('maintenance.records.machineLabel', { name: '' }).replace(':', '').trim() || 'Máquina'} <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                    type="text"
                    placeholder={t('maintenance.searchMachines', 'Buscar máquina...')}
                    value={machineSearch}
                    onChange={e => setMachineSearch(e.target.value)}
                    style={{ width: '100%', padding: '7px 12px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', marginBottom: '6px', boxSizing: 'border-box', outline: 'none', color: '#334155' }}
                />
                <div style={{ border: `1px solid ${!form.machine ? '#FCA5A5' : '#E2E8F0'}`, borderRadius: '6px', maxHeight: '160px', overflowY: 'auto' }}>
                    {availableMachines
                        .filter(m => m.name.toLowerCase().includes(machineSearch.toLowerCase()))
                        .map(m => (
                            <div
                                key={m.id}
                                onClick={() => setForm(prev => ({ ...prev, machine: String(m.id) }))}
                                style={{
                                    padding: '7px 10px', cursor: 'pointer',
                                    borderTop: '1px solid #F1F5F9', fontSize: '13px',
                                    color: parseInt(form.machine) === m.id ? '#4F46E5' : '#334155',
                                    background: parseInt(form.machine) === m.id ? '#EEF2FF' : 'transparent',
                                    fontWeight: parseInt(form.machine) === m.id ? 600 : 400,
                                }}
                            >
                                {m.name}
                            </div>
                        ))}
                    {availableMachines.filter(m => m.name.toLowerCase().includes(machineSearch.toLowerCase())).length === 0 && (
                        <div style={{ padding: '10px', color: '#94A3B8', fontSize: '13px', textAlign: 'center' }}>
                            {t('maintenance.noMachines', 'Sin resultados')}
                        </div>
                    )}
                </div>
                {form.machine && (
                    <div className="d-flex flex-wrap gap-1" style={{ marginTop: '6px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: '12px', background: '#EEF2FF', color: '#4F46E5', fontSize: '12px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            {allMachines.find(m => m.id === parseInt(form.machine))?.name}
                            <span style={{ cursor: 'pointer', fontSize: '10px', lineHeight: 1 }} onClick={() => setForm(prev => ({ ...prev, machine: '' }))}>×</span>
                        </span>
                    </div>
                )}
            </div>

            <div style={{ marginTop: '8px' }}>
                <label style={{ fontSize: '13px', color: '#64748B', display: 'block', marginBottom: '4px' }}>{t('maintenance.records.performedAt')}</label>
                <input
                    type="datetime-local"
                    value={form.performed_at}
                    onChange={e => setForm({ ...form, performed_at: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '14px' }}
                />
            </div>

            <div style={{ marginTop: '8px' }}>
                <label style={{ fontSize: '13px', color: '#64748B', display: 'block', marginBottom: '4px' }}>{t('maintenance.records.notesComments')}</label>
                <textarea
                    value={form.notes}
                    onChange={e => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '14px', resize: 'vertical' }}
                    placeholder={t('maintenance.records.notesPlaceholder')}
                />
            </div>

            {/* Photos — only in create mode */}
            {!isEdit && (
                <div style={{ marginTop: '12px' }}>
                    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                            <FontAwesomeIcon icon={faImage} style={{ marginRight: '5px' }} />{t('maintenance.records.photosLabel')}
                        </label>
                        <button
                            onClick={() => photoInputRef.current.click()}
                            style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '4px 10px', color: '#64748B', fontSize: '12px', cursor: 'pointer' }}
                        >
                            <FontAwesomeIcon icon={faUpload} /> {t('maintenance.records.addPhoto')}
                        </button>
                        <input ref={photoInputRef} type="file" accept="image/*" multiple hidden onChange={handlePhotoAdd} />
                    </div>
                    {photos.length > 0 && (
                        <div className="d-flex flex-wrap gap-2">
                            {photos.map((p, i) => (
                                <div key={i} style={{ position: 'relative' }}>
                                    <img src={p.preview} alt="" style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #E2E8F0' }} />
                                    <button
                                        onClick={() => removePhoto(i)}
                                        style={{
                                            position: 'absolute', top: '-6px', right: '-6px',
                                            background: '#DC2626', border: 'none', color: '#FFF',
                                            borderRadius: '50%', width: '18px', height: '18px',
                                            fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}
                                    >×</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Parts used */}
            <div style={{ marginTop: '12px' }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '8px' }}>
                    {t('maintenance.records.partsUsed')}
                </label>
                {prefilledFromPlan && parts.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#0369A1', background: '#F0F9FF', borderRadius: '6px', padding: '6px 10px', marginBottom: '8px' }}>
                        {t('maintenance.records.prefilledFromPlan')}
                    </div>
                )}

                {/* Add part row */}
                <div style={{ background: '#F8FAFC', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
                    <div style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '12px', color: '#64748B', marginBottom: '4px', display: 'block' }}>{t('maintenance.records.fromCatalog')}</label>
                        <select
                            value={newPart.spare_part}
                            onChange={e => selectCatalogPart(e.target.value)}
                            style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px' }}
                        >
                            {catalogOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        <input placeholder={t('maintenance.records.partNameRequired')} value={newPart.name} onChange={e => setNewPart({ ...newPart, name: e.target.value })}
                            style={{ flex: 2, minWidth: '120px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px' }} />
                        <input placeholder={t('maintenance.records.brand')} value={newPart.brand} onChange={e => setNewPart({ ...newPart, brand: e.target.value })}
                            style={{ flex: 1, minWidth: '80px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px' }} />
                        <input placeholder={t('maintenance.records.pn')} value={newPart.part_number} onChange={e => setNewPart({ ...newPart, part_number: e.target.value })}
                            style={{ flex: 1, minWidth: '70px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px' }} />
                        <input type="number" min="1" placeholder={t('maintenance.records.qtyPlaceholder')} value={newPart.quantity} onChange={e => setNewPart({ ...newPart, quantity: e.target.value })}
                            style={{ width: '60px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', textAlign: 'center' }} />
                        <button onClick={addPart} style={{
                            padding: '6px 12px', borderRadius: '6px', border: 'none',
                            background: '#4F46E5', color: '#FFF', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                        }}>
                            <FontAwesomeIcon icon={faPlus} /> {t('common.add')}
                        </button>
                    </div>
                </div>

                {/* Parts list */}
                {parts.length > 0 && (
                    <div style={{ borderRadius: '8px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                        <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#F1F5F9', borderBottom: '1px solid #E2E8F0' }}>
                                    <th style={{ padding: '7px 10px', textAlign: 'left', color: '#475569', fontWeight: 600 }}>{t('maintenance.records.part')}</th>
                                    <th style={{ padding: '7px 10px', textAlign: 'left', color: '#475569', fontWeight: 600 }}>{t('maintenance.records.brand')}</th>
                                    <th style={{ padding: '7px 10px', textAlign: 'left', color: '#475569', fontWeight: 600 }}>{t('maintenance.records.pn')}</th>
                                    <th style={{ padding: '7px 10px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>{t('maintenance.records.qty')}</th>
                                    <th style={{ padding: '7px 10px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {parts.map((p, i) => (
                                    <tr key={i} style={{ borderTop: i > 0 ? '1px solid #F1F5F9' : 'none', background: i % 2 === 0 ? '#FFF' : '#FAFAFA' }}>
                                        <td style={{ padding: '7px 10px', color: '#1E293B', fontWeight: 500 }}>{p.name}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748B' }}>{p.brand || '—'}</td>
                                        <td style={{ padding: '7px 10px', color: '#64748B', fontFamily: 'monospace', fontSize: '12px' }}>{p.part_number || '—'}</td>
                                        <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                                            <input
                                                type="number" min="1" value={p.quantity}
                                                onChange={e => setParts(prev => prev.map((part, j) => j === i ? { ...part, quantity: e.target.value } : part))}
                                                style={{ width: '55px', padding: '3px 6px', borderRadius: '5px', border: '1px solid #E2E8F0', fontSize: '13px', textAlign: 'center', fontWeight: 700, color: '#4F46E5' }}
                                            />
                                        </td>
                                        <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                                            <button onClick={() => removePart(i)} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', padding: '2px' }}>
                                                <FontAwesomeIcon icon={faTrash} style={{ fontSize: '11px' }} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Modal>
    );
}
